import { Box } from '@material-ui/core';
import { useEffect, useState } from 'react';

import { Action } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/_shared/types/PendingAction';
import { LoanPaymentMethodDetailEntity } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/_shared/entities/PaymentMethodDetail/LoanPaymentMethodDetailEntity';
import { isSignatureAction } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/NextActions/maps/isSignatureAction.map';
import { EditAutoPaymentMethodDialog } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Summary/Cart/Auto/components/EditAutoPaymentMethodDialog/EditAutoPaymentMethodDialog';
import { useCheckoutSessionAutoCart } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Summary/Cart/Auto/hooks/useCheckoutSessionAutoCart';
import { Font } from '@/components/shared/components/Font';
import { Text } from '@/components/shared/components/Text';
import { useUser } from '@/hooks/useUser';

interface SignatureMissingVehicleDataCaptionProps {
  paymentMethodDetailEntity: LoanPaymentMethodDetailEntity;
}

export const SignatureMissingVehicleDataCaption = ({
  paymentMethodDetailEntity,
}: SignatureMissingVehicleDataCaptionProps) => {
  const { isUserAdmin, isUserAgent, isUserMerchant, isUserChannel } = useUser();
  const { refreshCheckoutSessionAndPaymentMethodEntities } = useCheckoutSessionAutoCart();
  const [isEditVehicleDataDialogOpened, setIsEditVehicleDataDialogOpened] = useState(false);
  const [completedActions, setCompletedActions] = useState<Action[] | null>(null);

  const actions = paymentMethodDetailEntity.actions;

  useEffect(() => {
    const getCompletedActions = async () => {
      if (!actions || actions.length === 0) {
        return null;
      }

      const completedActionsResult = await Promise.all(
        actions.map(async (action) => ({
          action,
          isCompleted: await paymentMethodDetailEntity.isActionCompleted(action),
        })),
      );

      return completedActionsResult
        .filter((completedAction) => completedAction.isCompleted)
        .map((completedAction) => completedAction.action);
    };

    getCompletedActions().then(setCompletedActions);
  }, [actions, paymentMethodDetailEntity]);

  if (!completedActions || !actions?.length) {
    return null;
  }

  const firstUncompletedAction = actions.find((action) => !completedActions.includes(action));
  const isSignatureCurrentNextStep = isSignatureAction(firstUncompletedAction);

  if (!isSignatureCurrentNextStep) {
    return null;
  }

  if (!paymentMethodDetailEntity.isMissingVehicleRegistrationNumberOrLicensePlate()) {
    return null;
  }

  if (!paymentMethodDetailEntity.modificationCapability) {
    return null;
  }

  const isUserAllowedToEditVehicleData = isUserAdmin || isUserAgent || isUserMerchant || isUserChannel;
  const showEditableCaption = paymentMethodDetailEntity.isVehicleDataEditable() && isUserAllowedToEditVehicleData;

  return (
    <>
      <Box mt={2}>
        <Font variant="caption" color="textSecondary" display="block">
          {showEditableCaption ? (
            <Text
              tId="SIGNATURE_MISSING_VEHICLE_DATA_EDITABLE"
              linkCallbacks={[() => setIsEditVehicleDataDialogOpened(true)]}
            />
          ) : (
            <Text tId="SIGNATURE_MISSING_VEHICLE_DATA_NOT_EDITABLE" />
          )}
        </Font>
      </Box>
      {showEditableCaption && (
        <EditAutoPaymentMethodDialog
          entryMode="vehicle"
          checkoutSession={paymentMethodDetailEntity.checkoutSession}
          opened={isEditVehicleDataDialogOpened}
          setOpened={setIsEditVehicleDataDialogOpened}
          refreshCheckoutSessionAndPaymentMethodEntities={refreshCheckoutSessionAndPaymentMethodEntities}
        />
      )}
    </>
  );
};
